import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { findBulletinsData } from 'reducers/BulletinReducer';
import { getPeopleSops } from 'reducers/SearchReducer';
import { searchWarrants } from 'reducers/SearchReducer';
import { showEditWarrant, showWarrants } from 'reducers/DialogsReducer';

const useStyles = makeStyles((theme) => ({
  contentWrap: {
    minWidth: 800,
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  urgentMessageContainer: {
    width: '100%',
    margin: '15px 0 15px 15px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },

  urgentMessageLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#e0e0e0' : '#333333',
    marginRight: 4,
  },

  urgentMessageChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 16,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',

    '&:focus': {
      outline: 'none',
    },
  },

  // Active Warrants - Error
  urgentMessageChipError: {
    backgroundColor: theme.palette.type === 'dark' ? '#7f1d1d' : '#ffebee',
    color: theme.palette.type === 'dark' ? '#ffffff' : '#d32f2f',
    border: `1px solid ${theme.palette.type === 'dark' ? '#b71c1c' : '#ef9a9a'}`,

    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#991b1b' : '#ffcdd2',
    },

    '&:focus': {
      boxShadow: `0 0 0 2px ${theme.palette.type === 'dark' ? '#ef9a9a' : '#ef5350'}`,
    },
  },

  // SOP + Active Bulletins - Warning
  urgentMessageChipWarning: {
    backgroundColor: theme.palette.type === 'dark' ? '#78350f' : '#fff8e1',
    color: theme.palette.type === 'dark' ? '#ffffff' : '#ed6c02',
    border: `1px solid ${theme.palette.type === 'dark' ? '#b45309' : '#ffcc80'}`,

    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#92400e' : '#ffecb3',
    },

    '&:focus': {
      boxShadow: `0 0 0 2px ${theme.palette.type === 'dark' ? '#ffcc80' : '#ff9800'}`,
    },
  },

  urgentMessageChipCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    minWidth: 20,
    padding: 0,
    borderRadius: '50%',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    boxSizing: 'border-box',
  },

  urgentMessageChipCountError: {
    backgroundColor: theme.palette.type === 'dark' ? '#ffffff' : '#d32f2f',
    color: theme.palette.type === 'dark' ? '#7f1d1d' : '#ffffff',
  },

  urgentMessageChipCountWarning: {
    backgroundColor: theme.palette.type === 'dark' ? '#ffffff' : '#ed6c02',
    color: theme.palette.type === 'dark' ? '#78350f' : '#ffffff',
  },
}));

const UrgentMessage = (props) => {
  const {
    ptsPersonID,
    reloadUrgentMsg,
    onActiveWarrantsClick,
    onSopClick,
    onActiveBulletinsClick,
  } = props;
  const classes = useStyles();

  const urgentMsg = [
    {
      key: 'activeWarrants',
      title: 'Active Warrants',
      data: [],
      count: 0,
      onClick: onActiveWarrantsClick,
    },
    { key: 'sop', title: 'SOP', data: [], count: 0, onClick: onSopClick },
    {
      key: 'activeBulletins',
      title: 'Active Bulletins',
      data: [],
      count: 0,
      onClick: onActiveBulletinsClick,
    },
  ];

  const [urgentMessages, setUrgentMessages] = useState(urgentMsg);

  useEffect(() => {
    if (ptsPersonID) getUrgentMessage();
    else return null;
  }, [ptsPersonID, reloadUrgentMsg]);

  const totalCount = useMemo(() => {
    return urgentMessages.reduce((sum, item) => sum + item.count, 0);
  }, [urgentMessages]);

  const getActiveWarrants = async () => {
    return searchWarrants(
      {
        activeOnly: true,
        ptsPersonID,
      },
      0,
      25
    );
  };

  const getUrgentMessage = async () => {
    try {
      const [warrantsData, sopResponse, bulletinsData] = await Promise.all([
        getActiveWarrants(),
        getPeopleSops(ptsPersonID),
        findBulletinsData({ ptsPersonID }),
      ]);

      const urgentData = {
        activeWarrants: warrantsData ?? [],
        sop: sopResponse?.data ?? [],
        activeBulletins: bulletinsData ?? [],
      };

      setUrgentMessages((prev) =>
        prev.map((item) => {
          const data = urgentData[item.key];
          if (!data) return item;

          const updatedItem = {
            ...item,
            data,
            count: data.length,
          };

          return updatedItem;
        })
      );
    } catch (error) {
      console.error('Error fetching urgent messages:', error);
    }
  };

  const handleUrgentMessageClick = (message) => {
    if (message.key === 'activeWarrants') {
      props.showWarrants({ warrants: message.data });
    } else {
      message.onClick && message.onClick(message);
    }
  };

  return (
    <>
      {totalCount > 0 && (
        <div className={classes.urgentMessageContainer}>
          {urgentMessages.map(
            (message) =>
              message?.count > 0 && (
                <div
                  className={`${classes.urgentMessageChip} ${
                    message.key === 'activeWarrants'
                      ? classes.urgentMessageChipError
                      : classes.urgentMessageChipWarning
                  }`}
                  key={message.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleUrgentMessageClick(message)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleUrgentMessageClick(message);
                    }
                  }}>
                  <span>{message.title}</span>

                  <span
                    className={`${classes.urgentMessageChipCount} ${
                      message.key === 'activeWarrants'
                        ? classes.urgentMessageChipCountError
                        : classes.urgentMessageChipCountWarning
                    }`}>
                    {message.count}
                  </span>
                </div>
              )
          )}
        </div>
      )}
    </>
  );
};

export default connect(null, { showEditWarrant, showWarrants })(UrgentMessage);
